import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, ScanLine, Cpu, KeyRound, ChevronRight,
  AlertTriangle, CheckCircle2, FileText, Loader2,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCES = [
  { key: 'manual_upload', label: 'Manual Upload (Daily CSV)', icon: FileSpreadsheet,
    desc: 'One row per (employee, date). Upload CSV.' },
  { key: 'monthly_grid',  label: 'Monthly Grid (Excel)', icon: FileSpreadsheet,
    desc: 'One row per employee, columns = Day 1..N Status/In/Out. Upload XLSX.' },
  { key: 'biometric',     label: 'Biometric Import', icon: ScanLine,
    desc: 'Import biometric punch logs (eTimeTrackLite / eSSL CSV).' },
  { key: 'elite',         label: 'Elite Integration', icon: Cpu,
    desc: 'Import Elite/eTimeTrack flat-file exports.' },
  { key: 'api',           label: 'API Sync', icon: KeyRound,
    desc: 'Push attendance JSON via API token.' },
  { key: 'internal',      label: 'Internal Entry', icon: FileText,
    desc: 'HR direct entry — single date or month grid.' },
];

export default function AttendanceUpload() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [source, setSource] = useState('manual_upload');
  const [month, setMonth] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        const cs = Array.isArray(data) ? data : (data.companies || []);
        setCompanies(cs);
        if (cs.length === 1) setCompanyId(cs[0].id);
      } catch { /* */ }
    })();
  }, []);

  const handleTemplate = async () => {
    if (!companyId) { toast.error('Pick a company first'); return; }
    try {
      // Stage 73.20ct — Monthly Grid uses a separate XLSX template endpoint
      // that pre-loads employee roster + day columns for the picked month.
      if (source === 'monthly_grid') {
        if (!month) { toast.error('Pick a month first (YYYY-MM)'); return; }
        const res = await axios.get(
          `${API_URL}/attendance/monthly-grid/template?company_id=${companyId}&month=${month}`,
          { withCredentials: true, responseType: 'blob' },
        );
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url; a.download = `attendance_monthly_grid_${month}.xlsx`; a.click();
        window.URL.revokeObjectURL(url);
        return;
      }
      const res = await axios.get(`${API_URL}/attendance/bulk-upload/template?company_id=${companyId}`,
        { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'attendance_template.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Template download failed'); }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Pick a file'); return; }
    if (!companyId) { toast.error('Pick a company'); return; }

    // Stage 73.20ct — Monthly Grid upload uses a dedicated endpoint that
    // parses XLSX day-columns and expands into daily attendance_records.
    if (source === 'monthly_grid') {
      if (!month) { toast.error('Pick a month (YYYY-MM) to align the grid columns.'); return; }
      const fd = new FormData();
      fd.append('file', file);
      setUploading(true); setResult(null);
      try {
        const { data } = await axios.post(
          `${API_URL}/attendance/monthly-grid/upload?company_id=${companyId}&month=${month}`,
          fd,
          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
        );
        setResult({
          batch_number: `MG-${month}`,
          status: data.error_count > 0 ? 'partial' : 'completed',
          row_count: data.success_count + data.error_count,
          success_count: data.success_count,
          error_count: data.error_count,
          errors: (data.errors || []).map(e => ({
            row: e.row,
            error: `${e.employee_code || ''} Day ${e.day || '-'}: ${e.error}`,
          })),
        });
        if (data.error_count > 0) {
          toast.warning(`Uploaded with ${data.error_count} errors (${data.success_count} success, ${data.skipped_blank} blank cells)`);
        } else {
          toast.success(`Monthly grid uploaded — ${data.success_count} records`);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Upload failed');
      } finally {
        setUploading(false);
      }
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('company_id', companyId);
    fd.append('source_type', source);
    if (month) fd.append('month', month);
    if (notes) fd.append('notes', notes);

    setUploading(true); setResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/attendance/v2/upload`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      if (data.error_count > 0) {
        toast.warning(`Uploaded with ${data.error_count} errors`);
      } else {
        toast.success(`Batch ${data.batch_number} processed — ${data.success_count} records`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const SrcIcon = useMemo(() => SOURCES.find(s => s.key === source)?.icon || Upload, [source]);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="attendance-upload-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Upload className="text-blue-600" size={28} /> Attendance Upload / Import
        </h1>
        <p className="text-gray-600 mt-1">All attendance data lands in one common attendance table — by source.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {SOURCES.map(s => {
          const Icon = s.icon;
          const active = source === s.key;
          return (
            <button key={s.key} onClick={() => setSource(s.key)} data-testid={`source-${s.key}`}
              className={`p-3 rounded-xl border text-left transition ${active ? 'border-blue-500 bg-blue-50 shadow' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
              <Icon size={20} className={active ? 'text-blue-600' : 'text-slate-500'} />
              <div className="font-semibold mt-1 text-sm">{s.label}</div>
              <div className="text-xs text-slate-500 mt-1">{s.desc}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <SrcIcon size={20} className="text-blue-600" />
          <h3 className="font-semibold">{SOURCES.find(s => s.key === source)?.label}</h3>
        </div>

        {source === 'internal' ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
            Internal entry doesn't need a CSV. Use the dedicated Internal Attendance page
            (Attendance → Internal Attendance) for single-employee entry, month-grid editing,
            or single-cell overrides.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Company *</label>
                <select value={companyId} onChange={e => setCompanyId(e.target.value)}
                  data-testid="upload-company"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1 text-sm">
                  <option value="">— pick a company —</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Month (optional)</label>
                <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                  data-testid="upload-month"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Notes</label>
                <input value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="e.g. April manual entry"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1 text-sm" />
              </div>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-xs text-indigo-800" data-testid="wage-period-note">
              <b>Wage-period aware:</b> uploaded dates are automatically mapped to each employee's
              Employee Category wage period (e.g. 21st → 20th). Categories without a configured wage
              period follow the calendar month. Configure under Masters → Employee Categories.
              {source === 'monthly_grid' && " For the Monthly Grid, Day 1 = the first day of each employee's wage period for the selected month — the template's Instructions sheet shows the exact date mapping per category."}
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">
                {source === 'monthly_grid' ? 'Excel File (.xlsx) *' : 'CSV File *'}
              </label>
              <input
                type="file"
                accept={source === 'monthly_grid' ? '.xlsx' : '.csv'}
                onChange={e => setFile(e.target.files?.[0])}
                data-testid="upload-file"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1 text-sm"
              />
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button onClick={handleTemplate} className="text-sm text-blue-600 hover:underline">
                Download template
              </button>
              <span className="flex-1" />
              <button onClick={handleUpload} disabled={uploading || !file}
                data-testid="upload-submit"
                className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                Upload {SOURCES.find(s => s.key === source)?.label}
              </button>
            </div>
          </>
        )}
      </div>

      {result && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mt-5" data-testid="upload-result">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 size={18} className="text-emerald-600" /> Batch {result.batch_number}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Stat label="Status" value={result.status} />
            <Stat label="Rows" value={result.row_count} />
            <Stat label="Success" value={result.success_count} color="emerald" />
            <Stat label="Errors" value={result.error_count} color={result.error_count > 0 ? 'red' : 'slate'} />
          </div>
          {result.errors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-60 overflow-y-auto text-xs">
              <div className="font-semibold mb-2 text-red-700 flex items-center gap-1">
                <AlertTriangle size={12} /> First {result.errors.length} errors
              </div>
              {result.errors.map((e, i) => (
                <div key={i} className="border-b border-red-100 py-1 last:border-0">
                  <b>Row {e.row}:</b> {e.error}
                </div>
              ))}
            </div>
          )}
          <a href="/attendance/history" className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm mt-3">
            Go to Upload History <ChevronRight size={14} />
          </a>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color = 'slate' }) {
  const c = { slate: 'text-slate-700', red: 'text-red-700', emerald: 'text-emerald-700' }[color] || 'text-slate-700';
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-lg font-bold mt-1 ${c}`}>{String(value)}</div>
    </div>
  );
}
